import React from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
    useGetAdminBrandKnowledgeQuery,
    useLazyGetAdminBrandKnowledgeDocumentDownloadUrlQuery,
} from "../../services/nestjs-base-api";
import { IBrandKnowledgeProfile } from "../../services/nestjs-base-api";
import { useAuth } from "../../hooks/useAuth";

const { Title, Text, Paragraph } = Typography;

const RAW_FIELDS: { key: keyof IBrandKnowledgeProfile; label: string }[] = [
    { key: "positioning", label: "Positioning" },
    { key: "productsServices", label: "Products & Services" },
    { key: "targetCustomers", label: "Target Customers" },
    { key: "keyCompetitors", label: "Key Competitors" },
    { key: "toneOfVoice", label: "Tone of Voice" },
    { key: "brandGoals", label: "Brand Goals" },
    { key: "additionalContext", label: "Anything Else" },
];

interface IProps {
    brandId: number;
    /** Show the brand/org title header (off when the caller supplies its own). */
    showHeader?: boolean;
}

/**
 * SYR-78 — reusable read-only Brand Knowledge view: raw profile inputs, the exact
 * synthesised {BRAND_KNOWLEDGE} block, and the documents table. Used by the AI-hub
 * inspector detail page and the admin Organisation → Brand Knowledge tab.
 */
export const BrandKnowledgePanel: React.FC<IProps> = ({
    brandId,
    showHeader = true,
}) => {
    const { user } = useAuth();
    const { data, isLoading, error } = useGetAdminBrandKnowledgeQuery(
        { jwtToken: user?.jwtToken || "", brandId },
        { skip: !user?.jwtToken || !brandId }
    );
    const [triggerDownload] =
        useLazyGetAdminBrandKnowledgeDocumentDownloadUrlQuery();

    const handleDownload = async (documentId: number) => {
        try {
            const { url } = await triggerDownload({
                jwtToken: user?.jwtToken || "",
                brandId,
                documentId,
            }).unwrap();
            window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            message.error("Could not download file");
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: "center" }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <Alert
                type="error"
                showIcon
                message="Error loading brand knowledge"
                description="Unable to fetch this brand's knowledge base."
            />
        );
    }

    const { profile, documents, synthesised } = data;

    return (
        <>
            {showHeader && (
                <>
                    <Title level={3} style={{ marginTop: 0 }}>
                        Brand Knowledge — {data.brandName}
                    </Title>
                    <Text type="secondary">
                        {data.organisationName ??
                            `Organisation #${data.organisationId}`}
                    </Text>
                </>
            )}

            <Row gutter={[24, 24]} style={{ marginTop: showHeader ? 24 : 0 }}>
                <Col xs={24} lg={12}>
                    <Card title="Raw inputs">
                        {profile ? (
                            <Descriptions column={1} bordered size="small">
                                {RAW_FIELDS.map((field) => (
                                    <Descriptions.Item
                                        key={field.key}
                                        label={field.label}
                                    >
                                        {profile[field.key] ? (
                                            <span style={{ whiteSpace: "pre-wrap" }}>
                                                {profile[field.key] as string}
                                            </span>
                                        ) : (
                                            <Text type="secondary">—</Text>
                                        )}
                                    </Descriptions.Item>
                                ))}
                            </Descriptions>
                        ) : (
                            <Empty description="No brand knowledge profile has been created yet." />
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card
                        title="Synthesised {BRAND_KNOWLEDGE}"
                        extra={
                            <Space>
                                <Tag>{synthesised.tokenCount} tokens (approx)</Tag>
                                {synthesised.truncated && (
                                    <Tag color="orange">TRUNCATED</Tag>
                                )}
                                {synthesised.isEmpty && (
                                    <Tag color="default">EMPTY</Tag>
                                )}
                            </Space>
                        }
                    >
                        <Paragraph type="secondary">
                            This is exactly what is injected into the prompt where an
                            AI subsystem references {"{BRAND_KNOWLEDGE}"}.
                        </Paragraph>
                        {synthesised.isEmpty ? (
                            <Empty description="Nothing is injected — the brand has provided no context." />
                        ) : (
                            <pre
                                style={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    background: "#f5f5f5",
                                    padding: 16,
                                    borderRadius: 6,
                                    maxHeight: 600,
                                    overflow: "auto",
                                    margin: 0,
                                }}
                            >
                                {synthesised.markdown}
                            </pre>
                        )}
                    </Card>
                </Col>
            </Row>

            <Card title="Documents & websites" style={{ marginTop: 24 }}>
                <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={documents}
                    locale={{ emptyText: "No documents or websites added." }}
                    columns={[
                        { title: "Source", dataIndex: "sourceRef", key: "sourceRef" },
                        {
                            title: "Type",
                            dataIndex: "sourceType",
                            key: "sourceType",
                            render: (t: string) => (
                                <Tag color={t === "url_scrape" ? "blue" : "geekblue"}>
                                    {t === "url_scrape" ? "Website" : "Upload"}
                                </Tag>
                            ),
                        },
                        {
                            title: "Tokens",
                            dataIndex: "tokenCount",
                            key: "tokenCount",
                            render: (n: number) => `~${n}`,
                        },
                        {
                            title: "In context",
                            dataIndex: "includeInContext",
                            key: "includeInContext",
                            render: (v: boolean) =>
                                v ? (
                                    <Tag color="green">Yes</Tag>
                                ) : (
                                    <Tag color="default">Excluded</Tag>
                                ),
                        },
                        {
                            title: "Original",
                            key: "original",
                            align: "right" as const,
                            render: (_: unknown, doc: any) =>
                                doc.hasOriginal ? (
                                    <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownload(doc.id)}
                                    >
                                        Download
                                    </Button>
                                ) : (
                                    <Tag>None</Tag>
                                ),
                        },
                    ]}
                />
            </Card>
        </>
    );
};
